import React, { useEffect, useMemo, useState } from 'react';
import {
	Alert,
	Autocomplete,
	Avatar,
	Backdrop,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	ListItemAvatar,
	ListItemText,
	Snackbar,
	TextField,
} from '@mui/material';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import { makeStyles } from '@mui/styles';

import type { IUserList } from '../../../../../features/salesOrder/types';
import { defaultAlertState } from '../../../../../features/common/constants';
import type { ShowAlertState } from '../../../../../features/common/types';
import { changeSellerOfSaleOrder, getAllUsersActivesByAgencyId } from '../../../../../features/salesOrder/services';
import { extractErrorMessage } from '../../../../../features/quotation/helpers';

const useStyles = makeStyles((theme: any) => ({
	backdrop: {
		zIndex: theme.zIndex.modal + 1,
		color: '#fff',
	},
}));

type ChangeSaleOrderSellerModalProps = {
	open: boolean;
	agencyId: number;
	saleOrderId: string;
	currentSellerUserId: number;
	onClose: () => void;
	onSaved?: () => void;
};

const ChangeSaleOrderSellerModal: React.FC<ChangeSaleOrderSellerModalProps> = ({
	open,
	agencyId,
	saleOrderId,
	currentSellerUserId,
	onClose,
	onSaved,
}: ChangeSaleOrderSellerModalProps) => {
	const classes = useStyles();

	const [users, setUsers] = useState<IUserList[]>([]);
	const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
	const [loading, setLoading] = useState(false);
	const [alert, setAlert] = useState<ShowAlertState>(defaultAlertState);

	const selectedUser = useMemo(() => users.find((u) => u.id === selectedUserId) ?? null, [users, selectedUserId]);

	const canSave = selectedUserId !== null && selectedUserId !== currentSellerUserId && !loading;

	useEffect(() => {
		let cancelled = false;

		if (!open) {
			// reset
			setUsers([]);
			setSelectedUserId(null);
			setLoading(false);
			setAlert(defaultAlertState);
			return;
		}

		setLoading(true);

		getAllUsersActivesByAgencyId(agencyId)
			.then((res) => {
				if (cancelled) return;
				setUsers(res.data);
				setSelectedUserId(currentSellerUserId);
			})
			.catch((error) => {
				if (cancelled) return;
				setAlert({
					show: true,
					severity: 'error',
					message: extractErrorMessage(error, 'Error al cargar usuarios'),
				});
			})
			.finally(() => {
				if (cancelled) return;
				setLoading(false);
			});

		return () => {
			cancelled = true;
		};
	}, [open, agencyId, currentSellerUserId]);

	const handleSave = async () => {
		if (!canSave || selectedUserId == null) return;

		setLoading(true);

		try {
			await changeSellerOfSaleOrder(saleOrderId, selectedUserId);

			setAlert({
				show: true,
				severity: 'success',
				message: 'Vendedor cambiado correctamente',
			});

			setTimeout(() => {
				onSaved?.();
				onClose();
			}, 1200);
		} catch (error) {
			setAlert({
				show: true,
				severity: 'error',
				message: extractErrorMessage(error, 'Error al cambiar el vendedor'),
			});
		} finally {
			setLoading(false);
		}
	};

	return (
		<>
			<Dialog open={open} onClose={loading ? undefined : onClose} fullWidth maxWidth="sm">
				<DialogTitle>Cambiar vendedor de la sale order</DialogTitle>

				<DialogContent>
					{selectedUserId === currentSellerUserId && !selectedUser ? (
						<Alert severity="warning" sx={{ mb: 1, mt: 1 }}>
							El vendedor actual no está en la lista de usuarios activos.
						</Alert>
					) : null}

					<Autocomplete<IUserList>
						sx={{ mt: 1.5 }}
						options={users}
						value={selectedUser}
						onChange={(_, v) => setSelectedUserId(v?.id ?? null)}
						isOptionEqualToValue={(a, b) => a.id === b.id}
						getOptionLabel={(u) => u.name}
						renderOption={(props, option) => (
							<li {...props} key={option.id}>
								<ListItemAvatar>
									<Avatar sx={{ width: 32, height: 32 }}>
										<PersonOutlineIcon fontSize="small" />
									</Avatar>
								</ListItemAvatar>
								<ListItemText primary={option.name} secondary={option.email} />
							</li>
						)}
						renderInput={(params) => <TextField {...params} label="Nuevo vendedor" />}
					/>
				</DialogContent>

				<DialogActions>
					<Button onClick={onClose} disabled={loading}>
						Cancelar
					</Button>
					<Button variant="contained" disabled={!canSave} onClick={handleSave}>
						Guardar
					</Button>
				</DialogActions>

				<Backdrop className={classes.backdrop} open={loading}>
					<CircularProgress color="inherit" />
				</Backdrop>
			</Dialog>

			<Snackbar open={alert.show} autoHideDuration={3000} onClose={() => setAlert(defaultAlertState)}>
				<Alert severity={alert.severity} variant="filled">
					{alert.message}
				</Alert>
			</Snackbar>
		</>
	);
};

export default ChangeSaleOrderSellerModal;
